import { createContext, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Vector2 } from "three/src/math/Vector2";
import { Raycaster } from "three/src/core/Raycaster";
import axios from "axios";
import { LOCAL_URL } from "../../constant";
const ExperienceContext = createContext();
export function ExperienceProvider({ children }) {
	const [scene, setScene] = useState(null);
	const [camera, setCamera] = useState(null);
	const [viewport, setViewport] = useState(null);
	const centerRaycaster = new Raycaster();
	centerRaycaster.layers.set(3);
	const mouse = new Vector2(0, 0);
	const findCenter = () => {
		centerRaycaster.setFromCamera(mouse, camera);
		const intersection = centerRaycaster.intersectObjects(scene.children);
		if (intersection.length > 0) {
			return [
				intersection[0].point.x,
				intersection[0].point.y,
				intersection[0].point.z,
			];
		}
	};
	const getScale = (width, height) => {
		const adaptedHeight =
			height *
			(viewport.aspect > width / height
				? viewport.width / width
				: viewport.height / height);
		const adaptedWidth =
			width *
			(viewport.aspect > width / height
				? viewport.width / width
				: viewport.height / height);

		return [adaptedWidth * 200, adaptedHeight * 200, 1];
	};
	const [experience, setExperience] = useState([]);
	const saveExperience = (space) => {
		let tempSpace = { ...space };
		let tempExp = JSON.parse(tempSpace.Experiences);
		tempExp.panoramas = experience;
		axios.patch(LOCAL_URL + `/space/updateExperianceJson/${space.id}`, {
			Experiences: tempExp,
		});
	};
	const updateExperience = (experience) => {
		setExperience(experience);
	};
	const addPanorama = (name, panoUrl, thumbUrl, isPrimary) => {
		const newPanorama = {
			id: uuidv4(),
			isPrimary: isPrimary,
			name: name,
			url: panoUrl,
			thumbUrl: thumbUrl,
			cameraTarget: [0, 90],
			spots: [],
		};
		setExperience((prevState) => [...prevState, newPanorama]);
	};
	const updatePanoramaName = (panoId, name) => {
		const exp = [...experience];
		exp.find((exp) => exp.id === panoId).name = name;
		setExperience(exp);
	};
	const updatePanoramaDetails = (
		panoId,
		name,
		panoUrl,
		thumbUrl,
		isPrimary
	) => {
		const exp = [...experience];
		exp.find((exp) => exp.id === panoId).name = name;
		exp.find((exp) => exp.id === panoId).url = panoUrl;
		exp.find((exp) => exp.id === panoId).thumbUrl = thumbUrl;
		exp.find((exp) => exp.id === panoId).isPrimary = isPrimary;
		setExperience(exp);
	};
	const deletePanorama = (panoId) => {
		let exp = [...experience];
		exp = exp.filter((exp) => exp.id !== panoId);
		setExperience(exp);
	};
	const setCameraTarget = (panoId, target) => {
		const exp = [...experience];
		exp.find((exp) => exp.id === panoId).cameraTarget = target;
		setExperience(exp);
	};

	const changeHotpotLink = (panoId, spotId, linkId) => {
		const newExp = [...experience];
		experience
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).link = linkId;
		setExperience(newExp);
	};
	const changeHotpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};
	const changeHotspotName = (panoId, spotId, name) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).name = name;
		setExperience(newExp);
	};
	const addNewHotspot = (panoId) => {
		const newSpot = {
			id: uuidv4(),
			type: "hotspot",
			name: "",
			position: findCenter(),
			link: null,
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteHotspot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};

	const addNewTextspot = (panoId, text) => {
		const newSpot = {
			id: uuidv4(),
			type: "text",
			text: text,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteTextspot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeTextspotText = (panoId, spotId, text) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).text = text;
		setExperience(newExp);
	};
	const changeTextspotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addNewLinkspot = (panoId, link) => {
		const newSpot = {
			id: uuidv4(),
			type: "link",
			link: link,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteLinkspot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeLinkspotlink = (panoId, spotId, link) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).link = link;
		setExperience(newExp);
	};
	const changeLinkspotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addNewVideoPlane = (panoId, url, name, width, height) => {
		const scale = getScale(width, height);
		const rotation = [
			camera.rotation.x,
			camera.rotation.y,
			camera.rotation.z,
		];
		const newVideoPlane = {
			id: uuidv4(),
			type: "inline-video",
			url: url,
			name: name,
			isChroma: false,
			chromaKey: "rgb(0,255,0)",
			position: findCenter(),
			rotation: rotation,
			scale: scale,
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newVideoPlane);
		setExperience(newExp);
	};
	const changeVideoPlaneName = (panoId, spotId, name) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).name = name;
		setExperience(newExp);
	};
	const setVideoPlaneIsChroma = (panoId, spotId, isChroma) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).isChroma = isChroma;
		setExperience(newExp);
	};
	const setVideoPlaneChromaKey = (panoId, spotId, chromaKey) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).chromaKey = chromaKey;
		setExperience(newExp);
	};
	const deleteVideoPlane = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeVideoPlanePosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};
	const changeVideoPlaneRotation = (panoId, spotId, rotation) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).rotation = rotation;
		setExperience(newExp);
	};
	const changeVideoPlaneScale = (panoId, spotId, scale) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).scale = scale;
		setExperience(newExp);
	};

	const addNewInfoImageSpot = (panoId, url, thumb, text) => {
		const newSpot = {
			id: uuidv4(),
			type: "image",
			url: url,
			thumb: thumb,
			text: text,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteInfoImageSpot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeInfoImageSpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addNewAudioSpot = (panoId, url, text) => {
		const newSpot = {
			id: uuidv4(),
			type: "audio",
			url: url,
			text: text,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteAudioSpot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeAudioSpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addPanoSpot = (panoId, url, thumb, name) => {
		const newSpot = {
			id: uuidv4(),
			type: "pano",
			url: url,
			thumb: thumb,
			name: name,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deletePanoSpot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changePanoSpotName = (panoId, spotId, name) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).name = name;
		setExperience(newExp);
	};
	const changePanoSpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addVideoSpot = (panoId, url, thumb, name) => {
		const newSpot = {
			id: uuidv4(),
			type: "video",
			url: url,
			thumb: thumb,
			name: name,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteVideoSpot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeVideoSpotName = (panoId, spotId, name) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).name = name;
		setExperience(newExp);
	};
	const changeVideoSpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	const addThreeDSpot = (panoId, url, name) => {
		const newSpot = {
			id: uuidv4(),
			type: "three-d",
			url: url,
			name: name,
			position: findCenter(),
		};
		const newExp = [...experience];
		newExp.find((exp) => exp.id === panoId).spots.push(newSpot);
		setExperience(newExp);
	};
	const deleteThreeDSpot = (panoId, spotId) => {
		const newExp = [...experience];
		const newSpots = newExp
			.find((exp) => exp.id === panoId)
			.spots.filter((spot) => spot.id !== spotId);
		newExp.find((exp) => exp.id === panoId).spots = newSpots;
		setExperience(newExp);
	};
	const changeThreeDSpotName = (panoId, spotId, name) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).name = name;
		setExperience(newExp);
	};
	const changeThreeDSpotPosition = (panoId, spotId, position) => {
		const newExp = [...experience];
		newExp
			.find((exp) => exp.id === panoId)
			.spots.find((spot) => spot.id === spotId).position = position;
		setExperience(newExp);
	};

	return (
		<ExperienceContext.Provider
			value={{
				experience,
				updateExperience,
				setExperience,
				saveExperience,
				addPanorama,
				updatePanoramaName,
				updatePanoramaDetails,
				deletePanorama,
				setCameraTarget,
				addNewHotspot,
				deleteHotspot,
				changeHotspotName,
				changeHotpotLink,
				changeHotpotPosition,
				addNewTextspot,
				deleteTextspot,
				changeTextspotText,
				changeTextspotPosition,
				addNewLinkspot,
				deleteLinkspot,
				changeLinkspotlink,
				changeLinkspotPosition,
				addNewVideoPlane,
				deleteVideoPlane,
				changeVideoPlaneName,
				setVideoPlaneIsChroma,
				setVideoPlaneChromaKey,
				changeVideoPlanePosition,
				changeVideoPlaneRotation,
				changeVideoPlaneScale,
				addNewInfoImageSpot,
				deleteInfoImageSpot,
				changeInfoImageSpotPosition,
				addNewAudioSpot,
				deleteAudioSpot,
				changeAudioSpotPosition,
				addPanoSpot,
				deletePanoSpot,
				changePanoSpotName,
				changePanoSpotPosition,
				addVideoSpot,
				deleteVideoSpot,
				changeVideoSpotName,
				changeVideoSpotPosition,
				addThreeDSpot,
				deleteThreeDSpot,
				changeThreeDSpotName,
				changeThreeDSpotPosition,
				setCamera,
				setScene,
				setViewport,
			}}
		>
			{children}
		</ExperienceContext.Provider>
	);
}

export default ExperienceContext;
